import React, { useState, useEffect } from 'react';
import { Calculator, Users, LogOut, Percent } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

const BounceRateCalculator = () => {
  const [totalVisitors, setTotalVisitors] = useState(1000);
  const [bouncedVisitors, setBouncedVisitors] = useState(300);
  const [bounceRate, setBounceRate] = useState(0);

  useEffect(() => {
    calculateBounceRate();
  }, [totalVisitors, bouncedVisitors]);

  const calculateBounceRate = () => {
    const rate = (bouncedVisitors / totalVisitors) * 100;
    setBounceRate(parseFloat(rate.toFixed(2)));
  };

  const siteUrl = 'https://www.marketingcalculatorhub.com';
  const pageUrl = `${siteUrl}/bounce-rate-calculator`;
  const logoUrl = `${siteUrl}/logo.png`;

  return (
    <div className="container mx-auto px-4 py-8">
      <Helmet>
        <title>Bounce Rate Calculator: Reduce Website Exits | Free Tool</title>
        <meta name="description" content="Calculate your Website Bounce Rate instantly. Compare with industry benchmarks, get optimization tips, and improve visitor engagement." />
        <meta name="keywords" content="bounce rate calculator, website analytics, visitor engagement metrics, user behavior analysis, website optimization" />
        <link rel="canonical" href={pageUrl} />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:title" content="Free Bounce Rate Calculator: Optimize User Engagement" />
        <meta property="og:description" content="Calculate your Website Bounce Rate instantly. Get actionable insights to reduce bounces and improve visitor retention." />
        <meta property="og:image" content={logoUrl} />

        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content={pageUrl} />
        <meta property="twitter:title" content="Free Bounce Rate Calculator: Optimize User Engagement" />
        <meta property="twitter:description" content="Calculate your Website Bounce Rate instantly. Get actionable insights to reduce bounces and improve visitor retention." />
        <meta property="twitter:image" content={logoUrl} />

        {/* Schema.org JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebApplication",
            "name": "Bounce Rate Calculator",
            "description": "Free calculator to measure and optimize website bounce rates",
            "url": pageUrl,
            "image": logoUrl,
            "applicationCategory": "BusinessApplication",
            "operatingSystem": "Any",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "USD"
            },
            "featureList": [
              "Calculate Bounce Rate",
              "Industry Benchmarks",
              "Optimization Tips",
              "Performance Analysis",
              "Best Practices"
            ],
            "about": {
              "@type": "Thing",
              "name": "Bounce Rate",
              "description": "The percentage of visitors who navigate away from a website after viewing only one page."
            },
            "audience": {
              "@type": "Audience",
              "audienceType": "Website Owners, Digital Marketers, UX Designers"
            },
            "mainEntity": {
              "@type": "HowTo",
              "name": "How to Calculate Bounce Rate",
              "step": [
                {
                  "@type": "HowToStep",
                  "text": "Enter total number of visitors"
                },
                {
                  "@type": "HowToStep",
                  "text": "Input number of bounced visitors"
                },
                {
                  "@type": "HowToStep",
                  "text": "View your bounce rate and optimization tips"
                }
              ]
            }
          })}
        </script>
      </Helmet>

      <h1 className="text-3xl font-bold text-gray-800 mb-6 flex items-center">
        <Calculator className="w-8 h-8 mr-2 text-indigo-600" />
        Bounce Rate Calculator
      </h1>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Input Values</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Total Visitors</label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Users className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="number"
                  value={totalVisitors}
                  onChange={(e) => setTotalVisitors(Number(e.target.value))}
                  className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 pr-12 sm:text-sm border-gray-300 rounded-md"
                  placeholder="1000"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Bounced Visitors</label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <LogOut className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="number"
                  value={bouncedVisitors}
                  onChange={(e) => setBouncedVisitors(Number(e.target.value))}
                  className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 pr-12 sm:text-sm border-gray-300 rounded-md"
                  placeholder="300"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Results</h2>
          <div className="text-4xl font-bold text-indigo-600 mb-2 flex items-center">
            {bounceRate}%
            <Percent className="w-8 h-8 ml-2 text-indigo-400" />
          </div>
          <p className="text-gray-600">Bounce Rate</p>
        </div>
      </div>

      <div className="mt-8 bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">Interpretation & Industry Benchmarks</h2>
        <p className="text-gray-600">
          Your Bounce Rate is {bounceRate}%. This means that {bounceRate}% of visitors leave your website after viewing only one page.
        </p>

        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Industry Benchmarks</h3>
        <ul className="list-disc list-inside text-gray-600 mb-4">
          <li>E-commerce: 20-45%</li>
          <li>B2B: 25-55%</li>
          <li>Retail: 20-40%</li>
          <li>Landing Pages: 60-90%</li>
          <li>Blogs: 65-90%</li>
          <li>Service Sites: 10-30%</li>
          <li>Content Sites: 40-60%</li>
        </ul>

        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Bounce Rate Optimization</h3>
        <ul className="list-disc list-inside text-gray-600">
          <li>Improve page load speed</li>
          <li>Enhance website design</li>
          <li>Create engaging content</li>
          <li>Optimize for mobile</li>
          <li>Use clear CTAs</li>
          <li>Match user intent</li>
          <li>Implement internal linking</li>
          <li>Use exit-intent popups</li>
          <li>Improve navigation</li>
          <li>Test different layouts</li>
        </ul>

        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Common Bounce Triggers</h3>
        <ul className="list-disc list-inside text-gray-600">
          <li>Slow loading speed (40%)</li>
          <li>Poor mobile experience (30%)</li>
          <li>Irrelevant content (25%)</li>
          <li>Poor navigation (20%)</li>
          <li>Technical errors (15%)</li>
          <li>Intrusive popups (10%)</li>
        </ul>

        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Impact Analysis</h3>
        <ul className="list-disc list-inside text-gray-600">
          <li>1-second delay increases bounce rate by 32%</li>
          <li>Mobile optimization reduces bounces by 35%</li>
          <li>Clear navigation lowers bounce rate by 20%</li>
          <li>Relevant content decreases bounces by 28%</li>
          <li>Improved page speed reduces bounces by 40%</li>
        </ul>
      </div>
    </div>
  );
};

export default BounceRateCalculator;
